import type { Secret } from "./types";

/**
 * Filters secrets by search term (case-insensitive)
 * Searches through secret ID and key fields
 */
export const filterSecretsBySearch = (
  secrets: Secret[],
  searchTerm: string
): Secret[] => {
  if (!searchTerm.trim()) {
    return secrets;
  }

  const normalizedTerm = searchTerm.toLowerCase().trim();
  return secrets.filter(
    (secret) =>
      secret.id.toLowerCase().includes(normalizedTerm) ||
      secret.key.toLowerCase().includes(normalizedTerm)
  );
};

/**
 * Groups secrets by their binding type (project, tenant, static)
 */
export const groupSecretsByBinding = (secrets: Secret[]) => ({
  project: secrets.filter((secret) => secret.binding.type === "project"),
  tenant: secrets.filter((secret) => secret.binding.type === "tenant"),
  static: secrets.filter((secret) => secret.binding.type === "static"),
});

/**
 * Calculates override relationships between secrets
 * Override hierarchy: project > tenant > static
 */
export const calculateOverrides = (
  projectSecrets: Secret[],
  tenantSecrets: Secret[],
  staticSecrets: Secret[]
) => {
  const projectKeys = new Set(projectSecrets.map((s) => s.key));
  const tenantKeys = new Set(tenantSecrets.map((s) => s.key));
  const staticKeys = new Set(staticSecrets.map((s) => s.key));

  return {
    // Tenant secrets that are overridden by project secrets
    overriddenTenantSecrets: new Set(
      tenantSecrets.filter((s) => projectKeys.has(s.key)).map((s) => s.key)
    ),
    // Static secrets that are overridden by project OR tenant secrets
    overriddenStaticSecrets: new Set(
      staticSecrets
        .filter((s) => projectKeys.has(s.key) || tenantKeys.has(s.key))
        .map((s) => s.key)
    ),
    // Project secrets that override tenant secrets or global secrets
    overridingProjectSecrets: new Set(
      projectSecrets
        .filter((s) => tenantKeys.has(s.key) || staticKeys.has(s.key))
        .map((s) => s.key)
    ),
    // Tenant secrets that override static secrets (but are not overridden by project)
    overridingTenantSecrets: new Set(
      tenantSecrets
        .filter(
          (s) =>
            staticSecrets.some((staticSecret) => staticSecret.key === s.key) &&
            !projectKeys.has(s.key)
        )
        .map((s) => s.key)
    ),
  };
};
